import { useEffect, useState } from "react";
import { api } from "@/api";
import { Save, Plus, X, ChevronDown, ChevronUp } from "lucide-react";

/** Visual content editor for the about_page and home_page settings JSON.
 * Fully form-based — no JSON editing required. */
export default function PageContentEditor({ pageKey, title, schema }) {
  const [settings, setSettings] = useState(null);
  const [content, setContent] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [openSections, setOpenSections] = useState(() => Object.fromEntries(schema.map((s) => [s.key, true])));

  useEffect(() => {
    api.get("/admin/settings").then((r) => {
      setSettings(r.data);
      setContent(r.data?.[pageKey] || {});
    });
  }, [pageKey]);

  const setField = (path, value) => {
    setContent((prev) => {
      const next = JSON.parse(JSON.stringify(prev || {}));
      const parts = path.split(".");
      let cur = next;
      for (let i = 0; i < parts.length - 1; i++) {
        const p = parts[i];
        if (cur[p] === undefined || cur[p] === null) cur[p] = isNaN(parseInt(parts[i + 1])) ? {} : [];
        cur = cur[p];
      }
      cur[parts[parts.length - 1]] = value;
      return next;
    });
  };

  const getField = (path) => {
    const parts = path.split(".");
    let cur = content;
    for (const p of parts) {
      if (cur == null) return undefined;
      cur = cur[p];
    }
    return cur;
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", { [pageKey]: content });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } finally { setSaving(false); }
  };

  if (!settings) return <div className="p-8 text-[#4B5563] font-body">Loading…</div>;

  return (
    <div data-testid={`admin-${pageKey}`}>
      <div className="flex items-center justify-between mb-8 sticky top-0 bg-[#F5F5F5] py-4 z-10">
        <div>
          <h1 className="font-heading font-bold text-3xl text-[#1C1C1C]">{title}</h1>
          <p className="mt-1 font-body text-[#4B5563]">All sections below are live on the site. Save to publish.</p>
        </div>
        <div className="flex items-center gap-3">
          {saved && <span className="text-sm text-green-600 font-body">Saved.</span>}
          <button onClick={save} disabled={saving} className="btn-primary disabled:opacity-50" data-testid="page-save"><Save size={16} /> {saving ? "Saving…" : "Save"}</button>
        </div>
      </div>

      <div className="space-y-4">
        {schema.map((sec) => {
          const isOpen = openSections[sec.key];
          return (
            <div key={sec.key} className="bg-white border border-[#E5E7EB]">
              <button
                onClick={() => setOpenSections((o) => ({ ...o, [sec.key]: !isOpen }))}
                className="w-full px-6 py-4 flex items-center justify-between gap-4 border-b border-[#E5E7EB] text-left hover:bg-[#F5F5F5] transition-colors"
                data-testid={`sec-toggle-${sec.key}`}
              >
                <div>
                  <div className="font-heading font-bold text-lg text-[#1C1C1C]">{sec.title}</div>
                  {sec.hint && <div className="text-xs text-[#4B5563] font-body mt-1">{sec.hint}</div>}
                </div>
                {isOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
              </button>
              {isOpen && (
                <div className="p-6 space-y-4">
                  {sec.fields.map((f) => renderField(f, sec.key, content, setField, getField))}
                  {sec.list && (
                    <ListField
                      basePath={`${sec.key}.${sec.list.path}`}
                      itemFields={sec.list.itemFields}
                      label={sec.list.label}
                      defaults={sec.list.defaults}
                      content={content}
                      setField={setField}
                      getField={getField}
                    />
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function renderField(f, sectionKey, content, setField, getField) {
  const path = sectionKey === "_root" ? f.key : `${sectionKey}.${f.key}`;
  const value = getField(path);

  if (f.type === "textarea") {
    return (
      <Field key={f.key} label={f.label} hint={f.hint}>
        <textarea value={value || ""} onChange={(e) => setField(path, e.target.value)} rows={f.rows || 3} className="input-light" data-testid={`f-${path}`} />
      </Field>
    );
  }
  if (f.type === "boolean") {
    return (
      <label key={f.key} className="flex items-center gap-3 cursor-pointer" data-testid={`f-${path}`}>
        <input type="checkbox" checked={value !== false} onChange={(e) => setField(path, e.target.checked)} className="accent-[#C1121F] w-4 h-4" />
        <span className="font-heading font-medium text-sm text-[#1C1C1C]">{f.label}</span>
      </label>
    );
  }
  if (f.type === "paragraphs") {
    return <ParagraphsField key={f.key} label={f.label} hint={f.hint} value={value || []} onChange={(v) => setField(path, v)} testid={`f-${path}`} />;
  }
  if (f.type === "list") {
    return <SimpleListField key={f.key} label={f.label} hint={f.hint} value={value || []} onChange={(v) => setField(path, v)} testid={`f-${path}`} />;
  }
  return (
    <Field key={f.key} label={f.label} hint={f.hint}>
      <input type="text" value={value || ""} onChange={(e) => setField(path, e.target.value)} className="input-light" placeholder={f.placeholder} data-testid={`f-${path}`} />
    </Field>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider font-heading font-semibold text-[#4B5563] mb-2">{label}</label>
      {children}
      {hint && <div className="mt-1 text-xs text-[#A3A3A3] font-body">{hint}</div>}
    </div>
  );
}

function ParagraphsField({ label, hint, value, onChange, testid }) {
  return (
    <Field label={label} hint={hint}>
      <div className="space-y-2" data-testid={testid}>
        {value.map((p, i) => (
          <div key={i} className="grid grid-cols-[1fr_auto] gap-2">
            <textarea rows={3} value={p} onChange={(e) => onChange(value.map((x, j) => j === i ? e.target.value : x))} className="input-light" />
            <button type="button" onClick={() => onChange(value.filter((_, j) => j !== i))} className="px-3 text-[#C1121F] self-start"><X size={14} /></button>
          </div>
        ))}
        <button type="button" onClick={() => onChange([...value, ""])} className="btn-outline-dark text-sm"><Plus size={14} /> Add paragraph</button>
      </div>
    </Field>
  );
}

function SimpleListField({ label, hint, value, onChange, testid }) {
  const [v, setV] = useState("");
  return (
    <Field label={label} hint={hint}>
      <div data-testid={testid}>
        <div className="flex gap-2">
          <input type="text" value={v} onChange={(e) => setV(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); if (v.trim()) { onChange([...value, v.trim()]); setV(""); } } }} placeholder="Type and press Enter" className="input-light" />
          <button type="button" onClick={() => { if (v.trim()) { onChange([...value, v.trim()]); setV(""); } }} className="btn-outline-dark px-4">Add</button>
        </div>
        <ul className="mt-3 flex flex-wrap gap-2">
          {value.map((it, i) => (
            <li key={i} className="inline-flex items-center gap-2 bg-[#F5F5F5] px-3 py-1.5 text-sm font-body">
              {it}
              <button type="button" onClick={() => onChange(value.filter((_, j) => j !== i))} className="text-[#C1121F]"><X size={12} /></button>
            </li>
          ))}
        </ul>
      </div>
    </Field>
  );
}

function ListField({ basePath, itemFields, label, defaults, content, setField, getField }) {
  const items = getField(basePath) || [];
  const update = (i, k, v) => {
    const next = items.map((it, j) => j === i ? { ...it, [k]: v } : it);
    setField(basePath, next);
  };
  const add = () => setField(basePath, [...items, { ...defaults }]);
  const remove = (i) => setField(basePath, items.filter((_, j) => j !== i));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[i], next[j]] = [next[j], next[i]];
    setField(basePath, next);
  };

  return (
    <div className="border-t border-[#E5E7EB] pt-6 mt-6">
      <div className="flex items-center justify-between mb-4">
        <div className="font-heading font-bold text-base text-[#1C1C1C]">{label}</div>
        <button type="button" onClick={add} className="btn-outline-dark text-sm" data-testid={`list-add-${basePath}`}><Plus size={14} /> Add</button>
      </div>
      <div className="space-y-3">
        {items.map((item, i) => (
          <div key={i} className="border border-[#E5E7EB] p-4 bg-[#F5F5F5]">
            <div className="flex items-center justify-between mb-3">
              <div className="font-heading font-semibold text-sm text-[#1C1C1C]">Item {i + 1}</div>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => move(i, -1)} className="text-xs text-[#4B5563] hover:text-[#1C1C1C] px-2">↑</button>
                <button type="button" onClick={() => move(i, 1)} className="text-xs text-[#4B5563] hover:text-[#1C1C1C] px-2">↓</button>
                <button type="button" onClick={() => remove(i)} className="text-xs text-[#C1121F] px-2">Remove</button>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {itemFields.map((f) => (
                <div key={f.key} className={f.span === 2 ? "sm:col-span-2" : ""}>
                  <label className="block text-[10px] uppercase tracking-wider font-heading font-semibold text-[#4B5563] mb-1">{f.label}</label>
                  {f.type === "textarea" ? (
                    <textarea rows={3} value={item[f.key] || ""} onChange={(e) => update(i, f.key, e.target.value)} className="input-light" />
                  ) : (
                    <input type="text" value={item[f.key] || ""} onChange={(e) => update(i, f.key, e.target.value)} className="input-light" placeholder={f.placeholder} />
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="text-sm text-[#4B5563] font-body italic">No items yet — click Add to create one.</div>}
      </div>
    </div>
  );
}
